/**
 * profanity-blacklist.ts
 *
 * Substring-based profanity check for username validation.
 * Covers Russian mat (including common Latin transliterations) and English profanity.
 * Also applies light leet-speak normalization before checking.
 *
 * Frontend-only gate — enforced for UX.
 * Not enforced at the database/policy level because a 50+ word blacklist is
 * impractical in Tarobase policy expressions.
 */

/** Common leet-speak substitutions applied before matching */
function normalizeLeet(value: string): string {
  return value
    .toLowerCase()
    .replace(/0/g, 'o')
    .replace(/1/g, 'i')
    .replace(/3/g, 'e')
    .replace(/4/g, 'a')
    .replace(/5/g, 's')
    .replace(/7/g, 't')
    .replace(/@/g, 'a')
    .replace(/\$/g, 's');
}

/**
 * Blacklist of substrings to block.
 * All entries are lowercase. Matching is done after leet normalization.
 *
 * Russian mat — Cyrillic originals and common Latin transliterations.
 * English profanity — common slurs and obscenities.
 */
export const BLACKLIST: readonly string[] = [
  // ── Russian mat — Cyrillic ───────────────────────────────────────────────
  'хуй',
  'хуе',
  'хуя',
  'хуи',
  'хую',
  'пизда',
  'пизд',
  'ебать',
  'ебал',
  'ебан',
  'еби',
  'ёбан',
  'ёб',
  'блядь',
  'бляд',
  'блять',
  'сука',
  'суки',
  'суку',
  'мудак',
  'муда',
  'гондон',
  'пидор',
  'пидар',
  'залупа',
  'дрочить',
  'дроч',
  'ебло',
  'хер',
  'мразь',
  'шлюха',
  'шлюх',
  'выблядок',
  'гавно',
  'говно',
  'дерьмо',
  'сперма',
  'анал',
  'член',
  'чмо',
  'пиздец',
  'ёбаный',
  'ёбать',
  'еблан',
  'манда',
  'сучка',
  'ублюдок',
  'урод',
  'уёбок',
  'уебок',
  'залуп',
  'нахуй',
  'нахер',
  'ёбнутый',
  'ёбнут',
  'пиздун',
  'пиздабол',
  'ёбарь',
  'давалка',
  'шалава',
  'сучара',
  'пидр',
  'педик',
  'лох',
  'лохи',
  'мразота',
  // ── Russian mat — Latin transliterations ────────────────────────────────
  'khuy',
  'khui',
  'huy',
  'hui',
  'pizda',
  'pizdec',
  'pezdec',
  'ebat',
  'ebal',
  'eban',
  'yobany',
  'blyad',
  'bliad',
  'mudak',
  'gondon',
  'pidor',
  'pidar',
  'zalupa',
  'droch',
  'eblo',
  'kher',
  'shlyukha',
  'vyblyad',
  'gavno',
  'govno',
  'sperma',
  'chlen',
  'chmo',
  'pizdobol',
  'nahuy',
  'urod',
  'uebok',
  'uyebok',
  'suchka',
  'ublydok',
  'shalava',
  'pedik',
  'pidr',
  // ── English profanity ────────────────────────────────────────────────────
  'fuck',
  'fuk',
  'fck',
  'fuq',
  'fvck',
  'shit',
  'shyt',
  'bitch',
  'cunt',
  'asshole',
  'ashole',
  'dick',
  'pussy',
  'cock',
  'faggot',
  'nigger',
  'slut',
  'whore',
  'bastard',
  'motherfucker',
  'mofo',
  'retard',
  'nazi',
  'hitler',
  'twat',
  'wanker',
  'prick',
  'douchebag',
  'douche',
  'shithead',
  'dipshit',
  'jackass',
  'arsehole',
  'bollocks',
  'bugger',
  'porno',
  'porn',
  'rape',
  'rapist',
  'pedophile',
  'pedo',
  'nonce',
  'skank',
  'spic',
  'kike',
  'chink',
  'gook',
  'tranny',
  'kyke',
  'cracker',
  'honkey',
];

/**
 * Returns true if the given username contains any blacklisted substring.
 * Performs leet-speak normalization and case-insensitive matching.
 */
export function containsProfanity(username: string): boolean {
  const normalized = normalizeLeet(username);
  for (const word of BLACKLIST) {
    // Cyrillic words are checked on the original lowercased input (no leet in Cyrillic)
    const isCyrillic = /[\u0400-\u04FF]/.test(word);
    const target = isCyrillic ? username.toLowerCase() : normalized;
    if (target.includes(word)) return true;
  }
  return false;
}

/**
 * Returns a Russian error message if the username contains profanity, else null.
 */
export function getProfanityError(username: string): string | null {
  if (containsProfanity(username)) {
    return 'Никнейм содержит запрещённые слова';
  }
  return null;
}
